import React, { useEffect, useState } from 'react';
import { CloseableModal } from '../../../../components/CloseableModal';
import { BodyWrapper } from './BodyWrapper';
import { Footer } from './Footer';
import { ConnectEmailIntegrationModal } from '../../../SendEmailModal/ConnectEmailIntegrationModal';
import { SelectOption } from '../../../../components/Select';
import { AlertObject, AlertType } from '../../../../components/Alert';
import classNames from 'classnames';
import { AttachmentDto } from '../../../../services/dtos/EmailDto';
import { FileContent, useImperativeFilePicker } from 'use-file-picker';
import { EmailUserIntegration } from '../../../../entities/EmailUserIntegration';
import { UserService } from '../../../../services/v1/UserService';
import {
  isFailedIntegration,
  getConnectionEmailUrl,
} from '../../../../utils/espUserIntegration';
import { SignableOfferTemplate } from '../../../../entities/v1/applicant_tracking/SignableOfferTemplate';
import { SignableOfferService } from '../../../../services/v1/applicant_tracking/SignableOfferService';
import { SignableOffer } from '../../../../entities/v1/applicant_tracking/SignableOffer';
import { ApiError } from '../../../../services/ApiService/errors/ApiError';
import { getNounWithPossessiveCase } from '../../../../utils/grammar';
import { variablesToMap } from '../variables';
import { Signatory } from '../../../../entities/v1/applicant_tracking/Signatory';
import { FilesSizeValidator } from '../../../../utils/useFilePicker/filesSizeValidator';
import { ReconnectEmailIntegrationModalBody } from '../../../../components/ReconnectEmailIntegrationModalBody';
import { mapSignableOfferToForm } from './mapSignableOfferToForm';
import snakecaseKeys from 'snakecase-keys';

interface PropTypes {
  applicationId: number;
  hasEmailConnected: boolean;
  isOpen: boolean;
  to: string;
  offerRequisitionFlowEnabled: boolean;
  signableOffer?: SignableOffer;
  editMode?: boolean;
  onClose: (succedded?: boolean) => void;
  setAlert: (alert: AlertObject) => void;
}

export interface SignableOfferForm {
  // email related
  attachments: AttachmentDto[];
  to: string;
  cc?: SelectOption[];
  bcc?: SelectOption[];
  email?: string;
  emailJson?: string;
  subject?: string;
  subjectJson?: string;

  // offer form related
  signableOfferTemplate: SignableOfferTemplate;
  signatory?: Signatory;
  bonus?: number;
  bonusCurrency?: string;
  candidateName?: string;
  companyName?: string;
  directManager?: string;
  equity?: number;
  jobTitle?: string;
  offerExpirationDate?: string;
  paidTimeOff?: number;
  salary?: number;
  salaryCurrency?: string;
  senderName?: string;
  sickDays?: number;
  startingDate?: string;
  todayDate?: string;
  workingHours?: number;
  customVariables?: Map<string, any>;
}

const ATTACHMENT_MAX_FILES = 6;
const ATTACHMENT_MAX_SIZE_MB = 10;
const ATTACHMENT_MAX_FILES_SIZE_MB = 20;

function CreateOfferAlertMessage(props: { offer: SignableOffer }) {
  if (props.offer.status === 'requisition_pending') {
    return (
      <span>
        <b>
          {getNounWithPossessiveCase(
            props.offer.application?.candidate?.name || 'Candidate',
          )}
        </b>{' '}
        offer has been successfully submitted for approval.
      </span>
    );
  } else {
    return (
      <span>
        <b>
          {getNounWithPossessiveCase(
            props.offer.application?.candidate?.name || 'Candidate',
          )}
        </b>{' '}
        Offer for <b>{props.offer.application?.job?.name || 'the job'}</b> was
        successfully sent!
      </span>
    );
  }
}

async function handleSubmit(
  form: SignableOfferForm,
  variables: string[],
  signableOfferId: number,
  applicationId: number,
  onClose: (succedded?: boolean) => void,
  setAlert: (alert: AlertObject) => void,
  setLoading: (loading: boolean) => void,
) {
  setLoading(true);
  await SignableOfferService.upsert({
    id: signableOfferId,
    applicationId: applicationId,
    emailHtml: form.email,
    emailJson: form.emailJson,
    subjectJson: form.subjectJson,
    signableOfferTemplateId: form.signableOfferTemplate.id,
    signatoryId: form.signatory?.id,
    subjectText: form.subject,
    to: form.to,
    bcc: form.bcc?.map((c: SelectOption) => c.value),
    cc: form.cc?.map((c: SelectOption) => c.value),
    variables: JSON.stringify(snakecaseKeys(variablesToMap(form, variables))),
    attachments: form.attachments.map((attachment) => ({
      ...attachment,
      attachmentType: 'EmailAttachment',
    })),
  })
    .then((offer: SignableOffer) => {
      setAlert({
        message: <CreateOfferAlertMessage offer={offer} />,
        type: AlertType.Success,
      });
      setLoading(false);
      onClose(true);
    })
    .catch((e: ApiError) => {
      setAlert({
        message: e.body?.message || 'It was not possible to send the offer',
        type: AlertType.Danger,
      });
      setLoading(false);
      onClose(false);
    });
}

function buildTitle(
  step: number,
  failedIntegration: boolean,
  offerRequisitionFlowEnabled: boolean,
  editMode: boolean,
) {
  if (failedIntegration) {
    return 'Email Integration Expired';
  }

  if (offerRequisitionFlowEnabled) {
    const title =
      step === 3
        ? 'Offer Approval Requisition Summary'
        : 'Offer Approval Requisition';

    return editMode ? `Edit ${title}` : title;
  } else {
    return step === 3 ? 'Offer Summary' : 'Send Offer';
  }
}

export function SendSignableOfferModal(props: PropTypes) {
  const [form, setForm] = useState<SignableOfferForm>(() =>
    mapSignableOfferToForm(props.signableOffer),
  );
  const [step, setStep] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(false);
  const [validStep, setValidStep] = useState<boolean>(false);
  const [isValid, setIsValid] = useState<boolean>(false);
  const [variables, setVariables] = useState<string[]>(null);
  const [emailIntegration, setEmailIntegration] =
    useState<EmailUserIntegration>(null);
  const [emailIntegrationsLoading, setEmailIntegrationsLoading] =
    useState<boolean>(true);
  const hasEmailConnected =
    (emailIntegrationsLoading || props.hasEmailConnected) &&
    isFailedIntegration(emailIntegration) === false;
  const bodyIdentifier = 'offer-email-body';

  const handleRemoveFile = (index: number) => {
    const fileToDelete = form.attachments[index];
    const plainFileToDelete = plainFiles.find(
      (i) => i.name === fileToDelete.filename,
    );

    if (plainFileToDelete != null) {
      removeFileByReference(plainFileToDelete);
    }

    setForm((form) => ({
      ...form,
      attachments: form.attachments.filter((_i, idx) => idx !== index),
    }));
  };

  const [
    openFileSelector,
    {
      filesContent,
      errors,
      removeFileByReference,
      plainFiles,
      clear: clearUploadedFiles,
    },
  ] = useImperativeFilePicker({
    multiple: true,
    readAs: 'DataURL',
    maxFileSize: ATTACHMENT_MAX_SIZE_MB,
    readFilesContent: true,
    limitFilesConfig: {
      max: ATTACHMENT_MAX_FILES,
    },
    validators: [
      new FilesSizeValidator(
        form.attachments.map((a) => a.size),
        ATTACHMENT_MAX_FILES_SIZE_MB,
      ),
    ],
  });

  useEffect(() => {
    const newAttachmentDto = [...form.attachments];

    filesContent.forEach((fileContent: FileContent, index: number) => {
      if (
        newAttachmentDto.find((i) => fileContent.name == i.filename) == null
      ) {
        newAttachmentDto.push({
          size: plainFiles[index].size,
          filename: fileContent.name,
          contentBase64: fileContent.content,
        });
      }
    });

    setForm((form) => ({
      ...form,
      attachments: newAttachmentDto,
    }));

    setIsValid(validStep);
  }, [filesContent, validStep]);

  useEffect(() => {
    const emailBodyContainer = document.querySelector(`#${bodyIdentifier}`);
    if (emailBodyContainer) {
      emailBodyContainer.scrollTop = emailBodyContainer.scrollHeight;
    }
  }, [form.attachments]);

  useEffect(() => {
    props.isOpen &&
      UserService.showEmailIntegration()
        .then(setEmailIntegration)
        .finally(() => setEmailIntegrationsLoading(false));
  }, [props.isOpen]);

  return (
    <CloseableModal
      size='xl'
      isOpen={props.isOpen}
      className='modal-dialog-centered'
      onClose={props.onClose}
      headerTitle={buildTitle(
        step,
        isFailedIntegration(emailIntegration),
        props.offerRequisitionFlowEnabled,
        props.editMode,
      )}
      bodyClassName={classNames({ 'p-0': hasEmailConnected && step < 3 })}
      bodyChildren={
        isFailedIntegration(emailIntegration) ? (
          <ReconnectEmailIntegrationModalBody
            largeImage
            url={getConnectionEmailUrl()}
            onClose={props.onClose}
          />
        ) : hasEmailConnected ? (
          <BodyWrapper
            elementIdentifier={bodyIdentifier}
            applicationId={props.applicationId}
            form={form}
            isLoading={isLoading}
            to={props.to}
            step={step}
            variables={variables}
            offerRequisitionFlowEnabled={props.offerRequisitionFlowEnabled}
            editMode={props.editMode}
            clearUploadedFiles={clearUploadedFiles}
            setForm={setForm}
            setValidStep={setValidStep}
            files={plainFiles}
            removeAttachmentFileByIndex={handleRemoveFile}
            setVariables={setVariables}
          />
        ) : (
          <ConnectEmailIntegrationModal
            url={getConnectionEmailUrl()}
            onClose={() => {}}
          />
        )
      }
      footerChildren={
        hasEmailConnected && (
          <Footer
            files={plainFiles}
            isValid={isValid}
            isEmailIntegrationLoading={emailIntegrationsLoading}
            isLoading={isLoading}
            step={step}
            setStep={setStep}
            onClose={props.onClose}
            onSubmit={() =>
              handleSubmit(
                form,
                variables,
                props.signableOffer?.id,
                props.applicationId,
                () => {
                  setStep(0);
                  props.onClose();
                },
                props.setAlert,
                setIsLoading,
              )
            }
            openFileSelector={openFileSelector}
            attachmentErrors={errors}
            maxFiles={ATTACHMENT_MAX_FILES}
            maxFileSizeMb={ATTACHMENT_MAX_SIZE_MB}
          />
        )
      }
    />
  );
}
